<template>
  <div :class="['battle-log', isDark ? 'dark' : 'light']">
    <div v-if="recordings.length === 0" class="empty">
      暂无战斗记录
    </div>
    <div v-else class="log-list">
      <div
        v-for="(item, index) in recordings"
        :key="index"
        class="log-row"
      >
        <!-- 战斗记录 -->
        <template v-if="item.type === 'combat' || (!item.type && item.attacker)">
          <span class="round">第{{ item.round || (index + 1) }}回合</span>

          <!-- 攻击者 -->
          <span :class="item.identity == 0 ? 'player' : 'enemy'">
            {{ item.attacker }}
          </span>

          <!-- 技能/普攻 -->
          <span v-if="item.isSkill == 1" class="skill">[{{ item.skillName }}]</span>
          <span v-else class="normal">普攻</span>

          <!-- 目标 -->
          <span class="target">{{ item.hinjured }}</span>

          <!-- 伤害 -->
          <span class="damage">{{ formatDamage(item.hurt) }}</span>

          <!-- 伤害类型 -->
          <span v-if="item.attElement && item.attElement !== '无'"
                class="element"
                :style="{ color: getElementColor(item.attElement) }">
            {{ formatElement(item.attElement) }}
          </span>
          <span v-else class="damage-type">
            {{ item.hurtType === 1 ? '力道' : item.hurtType === 2 ? '灵力' : '' }}
          </span>

          <!-- 标记 -->
          <span v-if="item.isCritical == 1" class="tag crit">暴击</span>
          <span v-if="item.isCritical == 2" class="tag dodge">闪避</span>
          <span v-if="item.isCritical == 3" class="tag miss">未命中</span>
          <span v-if="item.isCombo === 1" class="tag combo">连击</span>

          <!-- 结果 -->
          <span class="result">
            剩{{ item.surplusHealth }}血
            <span v-if="item.surplusShieldValue > 0">/{{ item.surplusShieldValue }}盾</span>
            <span v-if="item.isDeath == 1" class="dead">[阵亡]</span>
          </span>

          <!-- 子事件（连击、反击、Buff等） -->
          <div v-if="item.hinjuredConmbatList && item.hinjuredConmbatList.length > 0" class="sub-events">
            <div v-for="(sub, idx) in item.hinjuredConmbatList" :key="idx" class="sub-row">
              <span class="sub-mark">└</span>

              <!-- 连击 -->
              <template v-if="sub.hurtType == 1">
                <span class="sub-tag combo">连击</span>
                <span>{{ sub.attacker }} → {{ sub.hinjured }}</span>
                <span class="damage">{{ sub.hurt }}</span>
                <span v-if="sub.isCritical == 1" class="tag crit">暴击</span>
                <span>剩{{ sub.surplusHealth }}血</span>
              </template>

              <!-- 反击 -->
              <template v-else-if="sub.hurtType == 4">
                <span class="sub-tag counter">反击</span>
                <span>{{ sub.attacker }}[{{ sub.skillName }}] → {{ sub.hinjured }}</span>
                <span class="damage">{{ sub.hurt }}</span>
              </template>

              <!-- 持续伤害 -->
              <template v-else-if="sub.hurtType == 3 || sub.hurtType == 6">
                <span class="sub-tag dot">持续</span>
                <span>{{ sub.skillName }}: {{ sub.hurt }}伤</span>
                <span>剩{{ sub.surplusHealth }}血</span>
              </template>

              <!-- 治疗 -->
              <template v-else-if="sub.hurtType == 5 || sub.hurtType == 9">
                <span class="sub-tag heal">治疗</span>
                <span>{{ sub.skillName }}: +{{ sub.hurt }}血</span>
                <span>当前{{ sub.surplusHealth }}</span>
              </template>

              <!-- Buff效果 -->
              <template v-else-if="sub.hurtType == 10 || sub.hurtType == 13 || sub.hurtType == 15 || sub.hurtType == 16 || sub.hurtType == 17">
                <span class="sub-tag buff">Buff</span>
                <span>{{ sub.hinjured }} {{ sub.skillName }}</span>
                <span v-if="sub.skillDesc">{{ sub.skillDesc }}</span>
              </template>

              <!-- 其他 -->
              <template v-else>
                <span>{{ sub.attacker }} → {{ sub.hinjured }}</span>
                <span v-if="sub.skillName">[{{ sub.skillName }}]</span>
                <span v-if="sub.hurt">{{ sub.hurt }}</span>
              </template>
            </div>
          </div>
        </template>

        <!-- 治疗记录 -->
        <template v-else-if="item.type === 'gain'">
          <span class="round">第{{ item.round || (index + 1) }}回合</span>
          <span class="heal">{{ item.attacker }} 治疗 {{ item.hinjured }} +{{ item.hurt }}</span>
          <span>剩{{ item.surplusHealth }}血</span>
        </template>
      </div>

      <!-- 战斗结果 -->
      <div v-if="lastResult" class="result-row">
        【{{ lastResult.result }}】 血{{ lastResult.reHealth }}/蓝{{ lastResult.reMana }} {{ lastResult.exp }} {{ lastResult.goods }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BattleProcess2",
  props: {
    recordings: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isDark: false
    };
  },
  computed: {
    lastResult() {
      // 找到最后一条 result 类型记录
      for (let i = this.recordings.length - 1; i >= 0; i--) {
        if (this.recordings[i].type === 'result') {
          return this.recordings[i];
        }
      }
      return null;
    }
  },
  created() {
    // 初始化主题
    this.initTheme();
    // 监听主题变化
    this.$bus && this.$bus.$on('themeChanged', this.handleThemeChange);
  },
  beforeDestroy() {
    this.$bus && this.$bus.$off('themeChanged', this.handleThemeChange);
  },
  methods: {
    initTheme() {
      this.isDark = localStorage.getItem("isTheme") === "black";
    },
    handleThemeChange(isDark) {
      this.isDark = isDark;
    },
    formatDamage(hurt) {
      if (!hurt && hurt !== 0) return '0';
      // 处理 "346,403" 这种格式
      return hurt.toString().replace(/,/g, '');
    },
    formatElement(element) {
      return element ? element.replace('属性伤害', '').replace('属性', '') : '';
    },
    getElementColor(element) {
      const colors = {
        '金': '#d88228',
        '木': '#04ad80',
        '水': '#0090ff',
        '火': '#9e0e42',
        '土': '#8b7355',
        '风': '#0fb479',
        '冰': '#078599',
        '雷': '#6a09f6'
      };
      return colors[element] || (this.isDark ? '#999' : '#666');
    }
  }
};
</script>

<style scoped>
/* 基础 */
.battle-log {
  font-family: "Microsoft YaHei", monospace;
  font-size: 13px;
  line-height: 1.6;
  padding: 10px;
  max-height: 400px;
  overflow-y: auto;
}

.log-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.log-row {
  padding: 4px 0;
  border-bottom: 1px dotted transparent;
}

/* 元素 */
.round {
  color: #888;
  margin-right: 8px;
}

.player {
  color: #4caf50;
  font-weight: 500;
}

.enemy {
  color: #9c27b0;
}

.target {
  color: #f44336;
  margin: 0 4px;
}

.damage {
  color: #ff9800;
  font-weight: bold;
  margin: 0 4px;
}

.skill {
  color: #2196f3;
  margin: 0 4px;
}

.normal {
  color: #888;
  margin: 0 4px;
}

.element, .damage-type {
  margin-right: 6px;
}

.tag {
  display: inline-block;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 11px;
  margin: 0 2px;
}

.crit {
  background: #ff5722;
  color: #fff;
}

.dodge, .miss {
  background: #4caf50;
  color: #fff;
}

.combo {
  background: #00bcd4;
  color: #fff;
}

.result {
  color: #888;
  margin-left: 8px;
  font-size: 12px;
}

.dead {
  color: #f44336;
  font-weight: bold;
}

.heal {
  color: #4caf50;
}

/* 子事件 */
.sub-events {
  margin-left: 20px;
  margin-top: 2px;
}

.sub-row {
  font-size: 12px;
  color: #666;
  padding: 2px 0;
}

.sub-mark {
  color: #999;
  margin-right: 4px;
}

.sub-tag {
  display: inline-block;
  padding: 0 3px;
  border-radius: 2px;
  font-size: 10px;
  margin-right: 4px;
}

.sub-tag.combo {
  background: #ff9800;
  color: #fff;
}

.sub-tag.counter {
  background: #673ab7;
  color: #fff;
}

.sub-tag.dot {
  background: #795548;
  color: #fff;
}

.sub-tag.heal {
  background: #4caf50;
  color: #fff;
}

.sub-tag.buff {
  background: #9c27b0;
  color: #fff;
}

/* 战斗结果 */
.result-row {
  margin-top: 10px;
  padding: 8px;
  text-align: center;
  font-weight: bold;
  border-radius: 4px;
}

.empty {
  text-align: center;
  color: #888;
  padding: 20px;
}

/* ========== 黑夜模式 ========== */
.dark {
  background: #1a1a1a;
  color: #ccc;
}

.dark .log-row {
  border-bottom-color: #333;
}

.dark .round {
  color: #555;
}

.dark .result {
  color: #666;
}

.dark .sub-row {
  color: #888;
}

.dark .result-row {
  background: #2a2a2a;
  color: #ffd700;
}

/* ========== 白天模式 ========== */
.light {
  background: #f5f5f5;
  color: #333;
}

.light .log-row {
  border-bottom-color: #ddd;
}

.light .round {
  color: #999;
}

.light .player {
  color: #2e7d32;
}

.light .enemy {
  color: #7b1fa2;
}

.light .target {
  color: #c62828;
}

.light .damage {
  color: #e65100;
}

.light .skill {
  color: #1565c0;
}

.light .normal {
  color: #757575;
}

.light .result {
  color: #757575;
}

.light .dead {
  color: #c62828;
}

.light .sub-row {
  color: #666;
}

.light .result-row {
  background: #fff3e0;
  color: #f57c00;
}
</style>



// WEBPACK FOOTER //
// src/components/battleProcess2.vue